// PaymentVerificationPanel.tsx - VERSIÓN REDISEÑADA
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { authFetch } from '../services/api';
import { Building2, ClipboardList, CheckCircle, XCircle, AlertTriangle, CalendarDays, Clock, Home, ArrowLeft } from 'lucide-react';
interface Payment {
  id: number;
  appointment_date: string;
  appointment_time: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  price: number;
  deposit_amount: number;
  metodo_pago_elegido: string;
  comprobante_pago_url: string;
  fecha_subida?: string;
  fecha_verificacion?: string;
  verificacion_notas?: string;
  service_name: string;
  service_duration?: number;
}

// ✅ NUEVO: Interface para turnos urgentes
interface UrgentPayment {
  id: number;
  client_name: string;
  appointment_date: string;
  appointment_time: string;
  days_until: number;
}

type TabType = 'pending' | 'approved' | 'rejected';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PaymentVerificationPanel: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // professionalId solo viene cuando el panel se abre desde el dashboard de
  // un asistente. Si no está, el backend asume que el user logueado es el
  // profesional. Cuando viene, lo propagamos a TODAS las llamadas como query string.
  const assistantProfessionalId = searchParams.get('professionalId');
  const isAssistantContext = Boolean(assistantProfessionalId);
  const appendProfessionalId = (url: string): string => {
    if (!isAssistantContext) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}professionalId=${assistantProfessionalId}`;
  };

  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [pendingPayments, setPendingPayments] = useState<Payment[]>([]);
  const [approvedPayments, setApprovedPayments] = useState<Payment[]>([]);
  const [rejectedPayments, setRejectedPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [notas, setNotas] = useState('');

  // ✅ Estado para turnos urgentes
  const [urgentPayments, setUrgentPayments] = useState<UrgentPayment[]>([]);
  const [showUrgentAlert, setShowUrgentAlert] = useState(true);

  useEffect(() => {
    loadPayments();
  }, [activeTab]);

  // ✅ NUEVO: Calcular turnos urgentes cuando cambian los pagos pendientes
  useEffect(() => {
    calculateUrgentPayments();
  }, [pendingPayments]);

  // ✅ NUEVO: Función para calcular turnos urgentes (mañana o pasado mañana)
  const calculateUrgentPayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const urgent = pendingPayments
      .map(payment => {
        // Parsear fecha manualmente para evitar timezone issues
        const parts = payment.appointment_date.substring(0, 10).split('-');
        const appointmentDate = new Date(
          parseInt(parts[0], 10),
          parseInt(parts[1], 10) - 1,
          parseInt(parts[2], 10),
          12, 0, 0
        );
        appointmentDate.setHours(0, 0, 0, 0);
        
        const diffTime = appointmentDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return {
          id: payment.id,
          client_name: payment.client_name,
          appointment_date: payment.appointment_date,
          appointment_time: payment.appointment_time,
          days_until: diffDays
        };
      })
      .filter(p => p.days_until >= 0 && p.days_until <= 2) // Hoy, mañana o pasado mañana
      .sort((a, b) => a.days_until - b.days_until);
    
    setUrgentPayments(urgent);
  };

  // ✅ NUEVO: Obtener texto de urgencia
  const getUrgencyText = (daysUntil: number): string => {
    switch (daysUntil) {
      case 0: return 'HOY';
      case 1: return 'MAÑANA';
      case 2: return 'PASADO MAÑANA';
      default: return `en ${daysUntil} días`;
    }
  };

  // ✅ NUEVO: Obtener color de urgencia
  const getUrgencyColor = (daysUntil: number): string => {
    switch (daysUntil) {
      case 0: return 'bg-red-500';
      case 1: return 'bg-orange-500';
      case 2: return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No se encontró token');
        alert('Sesión expirada. Por favor inicia sesión nuevamente.');
        return;
      }

      let endpoint = '';
      switch (activeTab) {
        case 'pending':
          endpoint = `${API_URL}/api/verification/pending`;
          break;
        case 'approved':
          endpoint = `${API_URL}/api/verification/approved`;
          break;
        case 'rejected':
          endpoint = `${API_URL}/api/verification/rejected`;
          break;
      }
      endpoint = appendProfessionalId(endpoint);

      const response = await axios.get(endpoint, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        switch (activeTab) {
          case 'pending':
            setPendingPayments(response.data.payments || []);
            break;
          case 'approved':
            setApprovedPayments(response.data.payments || []);
            break;
          case 'rejected':
            setRejectedPayments(response.data.payments || []);
            break;
        }
      }
    } catch (error: any) {
      console.error('❌ Error cargando pagos:', error);
      if (error.response?.status === 401) {
        // Intentar renovar token antes de mostrar error
        try {
          const refreshResponse = await authFetch(`${API_URL}/api/auth/refresh-token`, { method: 'POST' });
          if (refreshResponse.ok) {
            // Token renovado, reintentar carga
            loadPayments();
            return;
          }
        } catch {}
        alert('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
        window.location.href = '/login?session_expired=true';
      }
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de aprobación en vez de window.confirm
  const confirmApprovePayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowApproveModal(true);
  };

  const handleVerifyPayment = async (appointmentId: number, aprobado: boolean) => {
    if (!aprobado && !notas.trim()) {
      alert('Por favor ingresa el motivo del rechazo');
      return;
    }

    setVerifying(true);

    try {
      const token = localStorage.getItem('token');

      const endpoint = appendProfessionalId(
        aprobado
          ? `${API_URL}/api/verification/approve/${appointmentId}`
          : `${API_URL}/api/verification/reject/${appointmentId}`
      );

      const payload = aprobado ? {} : { reason: notas.trim() };

      const response = await axios.post(
        endpoint,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSelectedPayment(null);
        setShowRejectModal(false);
        setShowApproveModal(false);
        setShowImageModal(false);
        setNotas('');

        loadPayments();
      }
    } catch (error: any) {
      console.error('Error verificando pago:', error);
      alert(error.response?.data?.error || 'Error al verificar el pago');
    } finally {
      setVerifying(false);
    }
  };

const formatDate = (dateString: string) => {
    // Parsear la fecha manualmente para evitar problemas de timezone
    // La fecha viene como YYYY-MM-DD desde la base de datos
    let year: number, month: number, day: number;
    
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      // Extraer año, mes y día directamente del string
      const parts = dateString.substring(0, 10).split('-');
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1; // Los meses en JS van de 0-11
      day = parseInt(parts[2], 10);
    } else {
      // Fallback para otros formatos
      const date = new Date(dateString);
      year = date.getFullYear();
      month = date.getMonth();
      day = date.getDate();
    }
    
    // Crear fecha usando componentes locales (no UTC)
    const date = new Date(year, month, day, 12, 0, 0);
    
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    });
  };

  const getCurrentPayments = () => {
    switch (activeTab) {
      case 'pending':
        return pendingPayments;
      case 'approved':
        return approvedPayments;
      case 'rejected':
        return rejectedPayments;
      default:
        return [];
    }
  };

  const currentPayments = getCurrentPayments();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando pagos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
      {/* Header con botón volver */}
      <div className="mb-6">
        <div className="flex items-center mb-3 space-x-4">
          {isAssistantContext ? (
            <button
              onClick={() => navigate('/assistant/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Volver al panel de secretaria</span>
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate('/professional/calendar')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Volver al calendario</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/profesional')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100"
              >
                <Home className="w-4 h-4" />
                <span className="text-sm font-medium">Volver al panel principal</span>
              </button>
            </>
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Building2 className="w-8 h-8 text-gray-700" /> Verificación de Pagos
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Gestiona los comprobantes de pago de tus pacientes
        </p>
      </div>

      {/* ✅ NUEVO: Alerta de turnos urgentes */}
      {urgentPayments.length > 0 && showUrgentAlert && (
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 sm:p-5 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-bold text-amber-800 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" /> {urgentPayments.length} {urgentPayments.length === 1 ? 'turno requiere' : 'turnos requieren'} verificación urgente
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  Aprobá los comprobantes antes de las <strong>10:00 AM</strong> para que el paciente reciba el recordatorio de su turno.
                </p>
                
                {/* Lista de turnos urgentes */}
                <div className="space-y-2">
                  {urgentPayments.slice(0, 3).map((urgent) => (
                    <div 
                      key={urgent.id}
                      className="flex items-center justify-between bg-white bg-opacity-60 rounded-lg px-3 py-2 text-sm"
                    >
                      <div className="flex items-center space-x-2">
                        <span className={`${getUrgencyColor(urgent.days_until)} text-white text-xs font-bold px-2 py-0.5 rounded`}>
                          {getUrgencyText(urgent.days_until)}
                        </span>
                        <span className="font-medium text-gray-800">{urgent.client_name}</span>
                      </div>
                      <span className="text-gray-600 text-xs sm:text-sm">
                        {urgent.appointment_time}
                      </span>
                    </div>
                  ))}
                  {urgentPayments.length > 3 && (
                    <p className="text-xs text-amber-600 font-medium pl-2">
                      + {urgentPayments.length - 3} turnos más...
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Botón cerrar */}
            <button
              onClick={() => setShowUrgentAlert(false)}
              className="flex-shrink-0 text-amber-500 hover:text-amber-700 transition-colors ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Tabs - RESPONSIVE MEJORADO */}
      <div className="bg-white rounded-xl shadow-lg mb-6">
        <div className="border-b border-gray-200">
          {/* Versión Desktop - 3 columnas */}
          <nav className="hidden sm:flex -mb-px">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 py-4 px-4 text-center font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <ClipboardList className="w-5 h-5" />
                <span>Pendientes</span>
                {pendingPayments.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-full">
                    {pendingPayments.length}
                  </span>
                )}
                {/* ✅ NUEVO: Indicador de urgentes */}
                {urgentPayments.length > 0 && (
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 py-4 px-4 text-center font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Aprobados</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-1 py-4 px-4 text-center font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'border-b-2 border-red-500 text-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>Rechazados</span>
              </div>
            </button>
          </nav>

          {/* Versión Mobile - Tabs compactos horizontales con scroll */}
          <nav className="flex sm:hidden -mb-px overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium transition-colors whitespace-nowrap ${
                activeTab === 'pending'
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-500'
              }`}
            >
              <div className="flex items-center space-x-1.5">
                <ClipboardList className="w-4 h-4" />
                <span className="text-sm">Pendientes</span>
                {pendingPayments.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px]">
                    {pendingPayments.length}
                  </span>
                )}
                {/* ✅ NUEVO: Indicador de urgentes mobile */}
                {urgentPayments.length > 0 && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium transition-colors whitespace-nowrap ${
                activeTab === 'approved'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-500'
              }`}
            >
              <div className="flex items-center space-x-1.5">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Aprobados</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium transition-colors whitespace-nowrap ${
                activeTab === 'rejected'
                  ? 'border-b-2 border-red-500 text-red-600'
                  : 'text-gray-500'
              }`}
            >
              <div className="flex items-center space-x-1.5">
                <XCircle className="w-4 h-4" />
                <span className="text-sm">Rechazados</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Lista de pagos */}
      {currentPayments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 sm:p-12 text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
            ¡Todo al día!
          </h3>
          <p className="text-sm sm:text-base text-gray-500">
            No hay comprobantes pendientes de verificación
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {currentPayments.map((payment) => {
            // ✅ NUEVO: Calcular si es urgente para mostrar badge
            const parts = payment.appointment_date.substring(0, 10).split('-');
            const appointmentDate = new Date(
              parseInt(parts[0], 10),
              parseInt(parts[1], 10) - 1,
              parseInt(parts[2], 10),
              12, 0, 0
            );
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            appointmentDate.setHours(0, 0, 0, 0);
            const diffDays = Math.ceil((appointmentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            const isUrgent = activeTab === 'pending' && diffDays >= 0 && diffDays <= 2;
            
            return (
            <div 
              key={payment.id} 
              className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden ${
                isUrgent ? 'ring-2 ring-amber-400' : ''
              }`}
            >
              {/* ✅ NUEVO: Badge de urgencia */}
              {isUrgent && (
                <div className={`${getUrgencyColor(diffDays)} text-white text-xs font-bold px-3 py-1.5 flex items-center justify-center`}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  TURNO {getUrgencyText(diffDays)} - Verificar antes de las 10:00 AM
                </div>
              )}
              
              <div className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-6">
                  {/* Comprobante - Thumbnail */}
                  <div className="flex-shrink-0 mb-4 lg:mb-0">
                    <div 
                      onClick={() => {
                        setSelectedPayment(payment);
                        setShowImageModal(true);
                      }}
                      className="relative w-full h-48 sm:h-56 lg:w-48 lg:h-48 bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                    >
                      <img
                        src={payment.comprobante_pago_url}
                        alt="Comprobante"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                        <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Información del pago */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div className="flex-1 min-w-0 mr-3">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 truncate">
                          {payment.client_name}
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                          <span className="flex items-center truncate">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate">{payment.client_email}</span>
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {payment.client_phone}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xl sm:text-2xl font-bold text-green-600">
                          {formatPrice(
                            payment.deposit_amount && payment.deposit_amount > 0 
                              ? payment.deposit_amount 
                              : payment.price
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {payment.metodo_pago_elegido?.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4 text-sm sm:text-base">
                      <div className="flex items-center text-gray-700">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium truncate">{formatDate(payment.appointment_date)}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{payment.appointment_time}</span>
                      </div>
                      <div className="flex items-center text-gray-700 sm:col-span-2">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-teal-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                        <span className="font-medium truncate">{payment.service_name}</span>
                      </div>
                    </div>

                    {activeTab === 'rejected' && payment.verificacion_notas && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 sm:mb-4">
                        <p className="text-xs sm:text-sm font-medium text-red-800 mb-1">Motivo del rechazo:</p>
                        <p className="text-xs sm:text-sm text-red-700">{payment.verificacion_notas}</p>
                      </div>
                    )}

                    {/* Botones de acción - MOBILE OPTIMIZADO */}
                    {activeTab === 'pending' && (
                      <div className="space-y-2 sm:space-y-0 sm:flex sm:gap-3">
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowImageModal(true);
                          }}
                          className="w-full sm:flex-1 bg-blue-500 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center text-sm sm:text-base"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Ver
                        </button>

                        <button
                          onClick={() => confirmApprovePayment(payment)}
                          disabled={verifying}
                          className="w-full sm:flex-1 bg-green-500 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Aprobar
                        </button>

                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowRejectModal(true);
                            setNotas('');
                          }}
                          disabled={verifying}
                          className="w-full sm:flex-1 bg-red-500 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Rechazar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Modal de confirmación de aprobación */}
      {showApproveModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-green-50 border-b border-green-200 px-6 py-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-green-900">Aprobar transferencia</h3>
                  <p className="text-xs text-green-700">El turno se confirmará automáticamente</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">{selectedPayment.client_name}</span>
                  <span className="text-lg font-bold text-green-600">
                    {formatPrice(
                      selectedPayment.deposit_amount && selectedPayment.deposit_amount > 0
                        ? selectedPayment.deposit_amount
                        : selectedPayment.price
                    )}
                  </span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> {formatDate(selectedPayment.appointment_date)} • 
                    <Clock className="w-3.5 h-3.5 ml-1" /> {selectedPayment.appointment_time}
                  </p>
                  <p className="flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> {selectedPayment.service_name}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                ¿Confirmás que recibiste este pago por transferencia bancaria?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedPayment(null);
                  }}
                  disabled={verifying}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleVerifyPayment(selectedPayment.id, true)}
                  disabled={verifying}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {verifying ? 'Aprobando...' : <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" /> Sí, aprobar pago</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rechazo */}
      {showRejectModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Rechazar Pago
            </h3>
            
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>{selectedPayment.client_name}</strong>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {formatPrice(
                  selectedPayment.deposit_amount && selectedPayment.deposit_amount > 0 
                    ? selectedPayment.deposit_amount 
                    : selectedPayment.price
                )}
              </p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo del rechazo *
            </label>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej: El comprobante no es legible, el monto no coincide, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
              autoFocus
            />
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPayment(null);
                  setNotas('');
                }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                disabled={verifying}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (selectedPayment && notas.trim()) {
                    handleVerifyPayment(selectedPayment.id, false);
                  }
                }}
                disabled={verifying || !notas.trim()}
                className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? 'Rechazando...' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de imagen ampliada */}
      {showImageModal && selectedPayment && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="max-w-6xl w-full h-[90vh] flex flex-col relative">
            <div className="flex justify-between items-center mb-4 bg-black bg-opacity-50 p-4 rounded-t-xl flex-shrink-0">
              <div className="text-white">
                <h3 className="text-lg sm:text-xl font-semibold">
                  Comprobante de {selectedPayment.client_name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-300 mt-1">
                  {formatPrice(selectedPayment.deposit_amount || selectedPayment.price)} • {selectedPayment.metodo_pago_elegido?.toUpperCase()}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(false);
                }}
                className="text-white hover:text-gray-300 transition-colors bg-red-500 hover:bg-red-600 p-2 rounded-lg"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="bg-white p-4 rounded-lg flex-1 overflow-auto flex items-center justify-center">
              <img
                src={selectedPayment.comprobante_pago_url}
                alt="Comprobante ampliado"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            <div className="mt-4 bg-black bg-opacity-50 p-3 sm:p-4 rounded-b-xl flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
              <div className="text-white text-xs sm:text-sm text-center sm:text-left">
                <p className="flex items-center justify-center sm:justify-start gap-1.5">
                  <CalendarDays className="w-4 h-4" /> {formatDate(selectedPayment.appointment_date)} • 
                  <Clock className="w-4 h-4 ml-1" /> {selectedPayment.appointment_time}
                </p>
                <p className="text-gray-300 mt-1 flex items-center justify-center sm:justify-start gap-1.5">
                  <Building2 className="w-4 h-4" /> {selectedPayment.service_name}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(selectedPayment.comprobante_pago_url, '_blank');
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 text-sm"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span className="hidden sm:inline">Abrir original</span>
                  <span className="sm:hidden">Abrir</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowImageModal(false);
                  }}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default PaymentVerificationPanel;